import { AnalyticsService } from "./service";

/**
 * Tracks timing metrics throughout the checkout flow.
 *
 * Create one instance per checkout session and call the track methods
 * at appropriate points in the checkout flow.
 *
 * Usage:
 *   const requestId = getOrCreateCheckoutRequestId(environment);
 *   const tracker = new CheckoutTimingTracker(secureToken, requestId);
 *   tracker.trackPageReady();
 *   // ... iframe loads ...
 *   tracker.trackInputReady();
 *   // ... user submits ...
 *   tracker.trackSubmit();
 *   // ... API response ...
 *   tracker.trackSuccess(attemptId);
 */
export class CheckoutTimingTracker {
  // Class-level guard to prevent duplicate page_ready events across PaymentKit instances
  private static pageReadyTracked = false;

  private startTime: number;
  private checkoutSessionId: string;
  private requestId: string;
  private inputReadyTracked = false;
  // Perf-clock timestamp when input_ready fired, for ms_since_input_ready in the
  // submit-state snapshot. Undefined until the card input becomes ready.
  private inputReadyAtMs?: number;
  // Total VGS card-field mounts this session. Mount #1 is the initial mount; any later
  // mount is a remount (React strict-mode double-mount / teardown-remount).
  private mountCount = 0;
  // Fire-once guard for the first-input signal (mirrors inputReadyTracked).
  private firstInputTracked = false;

  constructor(checkoutSessionId: string, requestId: string) {
    this.startTime = performance.now();
    this.checkoutSessionId = checkoutSessionId;
    this.requestId = requestId;
  }

  /**
   * Track when SDK is initialized and ready.
   * Call immediately after PaymentKit() initialization.
   * Only tracks once per page load (subsequent calls are no-ops).
   */
  trackPageReady(): void {
    if (CheckoutTimingTracker.pageReadyTracked) return;
    CheckoutTimingTracker.pageReadyTracked = true;

    AnalyticsService.capture("checkout_page_ready", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track when card input iframe is loaded and ready for input.
   * Should only be called once (for first iframe, typically card_pan).
   * Subsequent calls are no-ops.
   */
  trackInputReady(): void {
    if (this.inputReadyTracked) return;
    this.inputReadyTracked = true;
    this.inputReadyAtMs = performance.now();

    AnalyticsService.capture("checkout_input_ready", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track a VGS card-field mount. Emits `card_vgs_mount` on the shared checkout
   * timeline (elapsed_ms since tracker creation) so mounts are orderable against the
   * other timing events. `trigger` is "initial" for the first mount of the session and
   * "remount" for any mount after a prior one (the React strict-mode / teardown case).
   */
  trackCardMount(): void {
    const trigger = this.mountCount === 0 ? "initial" : "remount";
    this.mountCount += 1;

    AnalyticsService.capture("card_vgs_mount", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
      trigger,
      mount_count: this.mountCount,
    });
  }

  /**
   * Track a VGS Collect CDN load failure — the card form never renders, so a later
   * submit fails with `card_*: required`. `reason` separates the 15s CDN timeout from a
   * script onerror; `elapsedMs` is how long the CDN load was awaited before failing.
   */
  trackCardMountFailed(reason: "cdn_timeout" | "cdn_error", elapsedMs: number): void {
    AnalyticsService.capture("card_vgs_mount_failed", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      reason,
      elapsed_ms: elapsedMs,
    });
  }

  /**
   * Track the shopper's first interaction with a VGS card field (first focus). Fires at
   * most once per session.
   */
  trackFirstInput(field: string): void {
    if (this.firstInputTracked) return;
    this.firstInputTracked = true;

    AnalyticsService.capture("card_vgs_first_input", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
      field,
    });
  }

  /**
   * Snapshot of the card-collection state at a required-field submit failure — the
   * telemetry for the chronic blank-form (`card_*: required`) failures. The caller
   * supplies the signals visible at the failure site; the tracker fills in the
   * session-level ones it owns (mount_count, input_ready_fired, ms_since_input_ready).
   * Per-field `*_set` are null when not determinable at the call site (e.g. before the
   * card setup intent is fetched).
   */
  trackSubmitState(snapshot: {
    vgsFormPresent: boolean;
    cdnLoaded: boolean;
    anyFieldFocused: boolean;
    panSet?: boolean | null;
    expSet?: boolean | null;
    cvcSet?: boolean | null;
  }): void {
    AnalyticsService.capture("card_submit_state", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      vgs_form_present: snapshot.vgsFormPresent,
      mount_count: this.mountCount,
      cdn_loaded: snapshot.cdnLoaded,
      input_ready_fired: this.inputReadyTracked,
      pan_set: snapshot.panSet ?? null,
      exp_set: snapshot.expSet ?? null,
      cvc_set: snapshot.cvcSet ?? null,
      any_field_focused: snapshot.anyFieldFocused,
      ms_since_input_ready: this.inputReadyAtMs != null ? Math.round(performance.now() - this.inputReadyAtMs) : null,
    });
  }

  /**
   * Track when user clicks submit button.
   */
  trackSubmit(): void {
    AnalyticsService.capture("checkout_submit", {
      checkout_session_id: this.checkoutSessionId,
      request_id: this.requestId,
      elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track successful checkout.
   *
   * @param checkoutAttemptId - The checkout attempt ID
   */
  trackSuccess(checkoutAttemptId: string): void {
    AnalyticsService.capture("checkout_success", {
      checkout_session_id: this.checkoutSessionId,
      checkout_attempt_id: checkoutAttemptId,
      request_id: this.requestId,
      total_elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Track failed checkout.
   *
   * @param checkoutAttemptId - The checkout attempt ID (may be null if failed early)
   * @param errorCode - The error code from the checkout response
   * @param errorMessage - The customer-facing error message
   */
  trackFail(checkoutAttemptId: string | null, errorCode: string | null, errorMessage: string | null): void {
    AnalyticsService.capture("checkout_fail", {
      checkout_session_id: this.checkoutSessionId,
      checkout_attempt_id: checkoutAttemptId,
      request_id: this.requestId,
      error_code: errorCode,
      error_message: errorMessage,
      total_elapsed_ms: this.getElapsedMs(),
    });
  }

  /**
   * Get elapsed time in milliseconds since tracker creation.
   */
  private getElapsedMs(): number {
    return Math.round(performance.now() - this.startTime);
  }
}
